import { useState } from 'react';

import {
  FreeInfluenzaFilter,
  initializeIsInfluenzaFiltersOpen,
  initializeSelectedInfluenzaFilters,
} from '@/parsers/f2f/common';
import { HospitalFreeInfluenzaType, HospitalInfluenzaFilter, InfluenzaFilterType } from '@/types/hospital';

export interface InfluenzaHospitalFilters {
  selectedInfluenzaFilters: {
    [K in HospitalInfluenzaFilter]: InfluenzaFilterType<K>;
  };
  isInfluenzaFiltersOpen: {
    [K in HospitalInfluenzaFilter]: boolean;
  };
  isInfluenzaFiltersSelected: boolean;
  isFreeInfluenzaFilterSelected: boolean;
  freeInfluenzaFilters: FreeInfluenzaFilter[];
  selectInfluenzaFilter: <K extends HospitalInfluenzaFilter>(type: K) => (value: InfluenzaFilterType<K>) => void;
  toggleInfluenzaFilter: <K extends HospitalInfluenzaFilter>(type: K) => () => void;
  resetInfluenzaFilters: () => void;
  getFreeInfluenzaFiltersProps: () => Pick<InfluenzaHospitalFilters, 'freeInfluenzaFilters'> & {
    onClickFilter: (selected: FreeInfluenzaFilter) => void;
    selectedFreeInfluenzaFilter: FreeInfluenzaFilter;
    closeBottomSheet: () => void;
  };
}

const useInfluenzaHospitalFilters = (): InfluenzaHospitalFilters => {
  const [selectedInfluenzaFilters, setSelectedInfluenzaFilters] = useState<{
    [K in HospitalInfluenzaFilter]: InfluenzaFilterType<K>;
  }>(initializeSelectedInfluenzaFilters());

  const [isInfluenzaFiltersOpen, setIsInfluenzaFiltersOpen] = useState<{
    [K in HospitalInfluenzaFilter]: boolean;
  }>(initializeIsInfluenzaFiltersOpen());

  const freeInfluenzaFilters = (Object.values(HospitalFreeInfluenzaType) as HospitalFreeInfluenzaType[]).map(
    (type) => new FreeInfluenzaFilter(type)
  );

  const isFreeInfluenzaFilterSelected = !!selectedInfluenzaFilters.FreeInfluenza.type;

  const isInfluenzaFiltersSelected = Object.values(selectedInfluenzaFilters).some((filter) => Boolean(filter.type));
  const selectInfluenzaFilter =
    <K extends HospitalInfluenzaFilter>(type: K) =>
    (value: InfluenzaFilterType<K>) => {
      setSelectedInfluenzaFilters({
        ...selectedInfluenzaFilters,
        [type]: value,
      });
      toggleInfluenzaFilter(type)();
    };

  const toggleInfluenzaFilter =
    <K extends HospitalInfluenzaFilter>(type: K) =>
    () => {
      setIsInfluenzaFiltersOpen({
        ...isInfluenzaFiltersOpen,
        [type]: !isInfluenzaFiltersOpen[type],
      });
    };

  const resetInfluenzaFilters = () => {
    setSelectedInfluenzaFilters(initializeSelectedInfluenzaFilters());
  };

  const getFreeInfluenzaFiltersProps = () => {
    return {
      freeInfluenzaFilters,
      onClickFilter: selectInfluenzaFilter(HospitalInfluenzaFilter.FreeInfluenza),
      selectedFreeInfluenzaFilter: selectedInfluenzaFilters.FreeInfluenza,
      closeBottomSheet: toggleInfluenzaFilter(HospitalInfluenzaFilter.FreeInfluenza),
    };
  };

  return {
    selectedInfluenzaFilters,
    isInfluenzaFiltersOpen,
    isInfluenzaFiltersSelected,
    freeInfluenzaFilters,
    isFreeInfluenzaFilterSelected,
    selectInfluenzaFilter,
    toggleInfluenzaFilter,
    resetInfluenzaFilters,
    getFreeInfluenzaFiltersProps,
  };
};

export default useInfluenzaHospitalFilters;
